import { useState } from 'react';

import {
    Button,
    Card,
    MonoLabel,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    Textarea,
    costValueColor,
} from '@/design-system';
import {
    REPORT_COST_KEYS,
    formatReportFullAddress,
    type Report,
    type ReportCostKey,
    type ReportFinding,
    type ReportFindingAdjustmentInput,
    type ReportSection,
} from '@/entities/report';
import { formatCurrency, formatDayLong } from '@/shared/lib';

import { reportCopy } from '../copy';
import { CostsPanel } from './CostsPanel';
import { SurveyJumpButton } from './SurveyJump';

export interface ReportDocumentProps {
    report: Report;
    section: ReportSection | null;
    /** Mag de tekst van dit rapport nog bij, en mag deze persoon dat? */
    editable: boolean;
    saving: boolean;
    saveError: string | null;
    /** `null` zet de gegenereerde tekst terug. */
    onSaveSection: (section: ReportSection, body: string | null) => void;
    logoUrl: string | null;
    /** Bedragen, NHG-keuze en NHG-onderdeel van een bevinding aanpassen. */
    onAdjustFinding: (
        finding: ReportFinding,
        input: ReportFindingAdjustmentInput,
    ) => Promise<unknown>;
    /** Het akkoord van de inspecteur met zo'n aanpassing. */
    onConfirmFinding: (finding: ReportFinding) => Promise<unknown>;
}

/** Bij deze hoofdstukken hoort het overzicht Kosten & NHG in plaats van de bevindingentabel. */
const COST_SECTIONS: ReadonlySet<ReportSection['kind']> = new Set(['costs', 'nhg']);

const COST_LABELS: Record<ReportCostKey, string> = {
    direct: reportCopy.costDirect,
    termijn: reportCopy.costTermijn,
    onderhoud: reportCopy.costOnderhoud,
};

/**
 * Een eigen tekst doet er in de PDF alleen toe bij deze hoofdstukken. "Zo leest
 * u dit rapport" staat er bewust niet bij: die uitleg is in elk rapport gelijk.
 */
const TEXT_SECTIONS: ReadonlySet<ReportSection['kind']> = new Set([
    'summary',
    'costs',
    'part',
    'conditions',
]);

/** De API accepteert hoogstens zoveel tekens per hoofdstuk. */
const MAX_BODY_LENGTH = 20_000;

/**
 * Het documentvlak: hoe het rapport er straks uitziet. De eigenaar leest na en
 * stelt hoogstens een tekst of een bedrag bij - er wordt hier niets opgebouwd
 * (principe 5). Bij Kosten en NHG-bijlage staat het overzicht Kosten & NHG.
 */
export function ReportDocument({
    report,
    section,
    editable,
    saving,
    saveError,
    onSaveSection,
    logoUrl,
    onAdjustFinding,
    onConfirmFinding,
}: ReportDocumentProps) {
    const meta = reportCopy.reportMeta(
        report.clientName,
        report.inspectedOn ? formatDayLong(report.inspectedOn) : null,
        report.inspectorName,
    );

    return (
        <Card className="min-w-0 max-w-[780px] flex-[1_1_460px] px-[clamp(18px,4vw,38px)] py-[34px]">
            <header className="flex items-start justify-between gap-4 border-b-2 border-[var(--accent)] pb-5">
                <div className="min-w-0">
                    <MonoLabel size={10} tone="muted" as="div" className="tracking-[0.1em]">
                        {reportCopy.reportHeading(report.kindLabel, report.normLabel)}
                    </MonoLabel>
                    <h3 className="m-0 mt-2 font-[family-name:var(--font-display)] text-[26px] font-semibold leading-[1.2] tracking-[-0.02em] text-[var(--text-primary)]">
                        {formatReportFullAddress(report.address)}
                    </h3>
                    {meta ? (
                        <p className="m-0 mt-[6px] text-[13px] text-[var(--text-tertiary-safe)]">
                            {meta}
                        </p>
                    ) : null}
                </div>
                {logoUrl ? (
                    <img
                        src={logoUrl}
                        alt=""
                        className="h-[46px] w-auto max-w-[140px] shrink-0 object-contain"
                    />
                ) : (
                    <Skeleton
                        variant="hatch"
                        width={92}
                        height={46}
                        radius={4}
                        bordered
                        className="shrink-0 items-center justify-center"
                        caption={reportCopy.logoCaption}
                    />
                )}
            </header>

            <div className="my-[22px] grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(146px,1fr))]">
                {REPORT_COST_KEYS.map((key) => (
                    <div
                        key={key}
                        className="rounded-[8px] border border-[var(--border-default)] p-[14px]"
                    >
                        <div className="text-[12px] text-[var(--text-tertiary-safe)]">
                            {COST_LABELS[key]}
                        </div>
                        <div
                            className="mt-[5px] font-[family-name:var(--font-display)] text-[22px] font-semibold leading-[1.1]"
                            style={{ color: costValueColor[key] }}
                        >
                            {formatCurrency(report.costs[key])}
                        </div>
                    </div>
                ))}
            </div>

            <p className="m-0 mb-5 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {[
                    reportCopy.countsLine(
                        report.counts.findings,
                        report.counts.defects,
                        report.counts.attentionPoints,
                    ),
                    reportCopy.partsLine(report.counts.parts, report.counts.partsWithoutFindings),
                    reportCopy.questionsLine(
                        report.counts.questionsAnswered,
                        report.counts.questionsVisible,
                    ),
                ].join(' · ')}
            </p>

            {section ? (
                <SectionBlock
                    /* Wisselt de sectie of komt er een nieuwe tekst van de server,
                       dan begint de editor schoon - zonder effect. */
                    key={`${section.uuid}:${section.bodyUpdatedAt ?? ''}:${section.body ?? ''}`}
                    section={section}
                    editable={editable && TEXT_SECTIONS.has(section.kind)}
                    locked={!editable && !report.editable}
                    saving={saving}
                    saveError={saveError}
                    onSave={onSaveSection}
                />
            ) : null}

            {section && COST_SECTIONS.has(section.kind) ? (
                <CostsPanel
                    report={report}
                    onAdjust={onAdjustFinding}
                    onConfirm={onConfirmFinding}
                />
            ) : (
                <FindingsWithCosts report={report} />
            )}
        </Card>
    );
}

/**
 * De bevindingen met kosten en de foto's, onder elk hoofdstuk behalve de kosten.
 */
function FindingsWithCosts({ report }: { report: Report }) {
    const priced = report.findings.filter((finding) => finding.cost > 0);

    return (
        <>
            <div className="mb-2 overflow-hidden rounded-[8px] border border-[var(--border-default)]">
                {priced.length === 0 ? (
                    <p className="m-0 p-3 text-[13px] text-[var(--text-tertiary-safe)]">
                        {reportCopy.findingsEmpty}
                    </p>
                ) : (
                    <Table
                        columns="1.4fr 1fr 96px 96px 132px"
                        minWidth={620}
                        label={reportCopy.tableLabel}
                    >
                        <TableHead className="font-[family-name:var(--font-body)] text-[11px] tracking-[0.04em]">
                            <TableHeaderCell>{reportCopy.columnPart}</TableHeaderCell>
                            <TableHeaderCell>{reportCopy.columnFinding}</TableHeaderCell>
                            <TableHeaderCell>{reportCopy.columnUrgency}</TableHeaderCell>
                            <TableHeaderCell align="end">{reportCopy.columnCost}</TableHeaderCell>
                            <TableHeaderCell>{reportCopy.surveyJump.columnSurvey}</TableHeaderCell>
                        </TableHead>
                        <TableBody>
                            {priced.map((finding) => (
                                <TableRow key={finding.key}>
                                    <TableCell emphasis>
                                        {finding.part || finding.section}
                                    </TableCell>
                                    <TableCell className="text-[var(--text-reading)]">
                                        {finding.finding}
                                    </TableCell>
                                    <TableCell muted>{finding.urgency}</TableCell>
                                    <TableCell mono align="end">
                                        {formatCurrency(finding.cost)}
                                    </TableCell>
                                    <TableCell>
                                        <SurveyJumpButton
                                            sectionPath={finding.sectionPath}
                                            what={finding.finding}
                                            variant="short"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
            <p className="m-0 mb-5 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                {reportCopy.tableNote}
            </p>

            {report.photos.length > 0 ? (
                <ul
                    aria-label={reportCopy.photosLabel}
                    className="m-0 grid list-none gap-3 p-0 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]"
                >
                    {report.photos.map((photo) => (
                        <li key={photo.uuid} className="m-0">
                            <figure className="m-0">
                                <img
                                    src={photo.url}
                                    alt={photo.caption}
                                    loading="lazy"
                                    className="block h-[148px] w-full rounded-[8px] border border-[var(--placeholder-border)] object-cover"
                                />
                                {photo.caption ? (
                                    <figcaption className="mt-[6px] font-[family-name:var(--font-mono)] text-[10px] leading-[1.4] text-[var(--text-muted-mono-safe)]">
                                        {photo.caption}
                                    </figcaption>
                                ) : null}
                            </figure>
                        </li>
                    ))}
                </ul>
            ) : null}
        </>
    );
}

/**
 * Eén hoofdstuk in het document. Vóór goedkeuring is de tekst ter plekke bij te
 * stellen of terug te zetten naar de gegenereerde; daarna staat hij vast.
 */
function SectionBlock({
    section,
    editable,
    locked,
    saving,
    saveError,
    onSave,
}: {
    section: ReportSection;
    editable: boolean;
    locked: boolean;
    saving: boolean;
    saveError: string | null;
    onSave: (section: ReportSection, body: string | null) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(section.body ?? '');

    const empty =
        section.kind === 'nhg'
            ? reportCopy.nhgNoBody
            : section.kind === 'part'
              ? reportCopy.partNoBody
              : null;

    return (
        <section className="mb-5">
            <div className="mb-[10px] flex flex-wrap items-center justify-between gap-3">
                <h4 className="m-0 font-[family-name:var(--font-display)] text-[16px] font-semibold leading-[1.3] text-[var(--text-primary)]">
                    {section.label}
                </h4>
                {editing ? null : (
                    <div className="flex flex-wrap gap-2">
                        {/* Het antwoord zelf zit in de opname; deze knop brengt je erheen. */}
                        <SurveyJumpButton sectionPath={section.sectionPath} what={section.label} />
                        {editable && !section.bodyIsGenerated ? (
                            <Button
                                variant="outline"
                                size="xs"
                                disabled={saving}
                                onClick={() => onSave(section, null)}
                            >
                                {reportCopy.restore}
                            </Button>
                        ) : null}
                        {editable ? (
                            <Button variant="outline" size="xs" onClick={() => setEditing(true)}>
                                {reportCopy.edit}
                            </Button>
                        ) : null}
                    </div>
                )}
                {locked ? (
                    <span className="text-[12px] text-[var(--text-tertiary-safe)]">
                        {reportCopy.editLocked}
                    </span>
                ) : null}
            </div>

            {editing ? (
                <>
                    <Textarea
                        size="md"
                        rows={8}
                        maxLength={MAX_BODY_LENGTH}
                        aria-label={reportCopy.editLabel(section.label)}
                        aria-describedby={`hint-${section.uuid}`}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                    />
                    <p
                        id={`hint-${section.uuid}`}
                        className="m-0 mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]"
                    >
                        {reportCopy.editHint}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            variant="primary"
                            size="md"
                            disabled={saving}
                            onClick={() => {
                                onSave(section, draft.trim() === '' ? null : draft);
                                setEditing(false);
                            }}
                        >
                            {reportCopy.save}
                        </Button>
                        <Button
                            variant="outline"
                            size="md"
                            disabled={saving}
                            onClick={() => {
                                setDraft(section.body ?? '');
                                setEditing(false);
                            }}
                        >
                            {reportCopy.cancel}
                        </Button>
                    </div>
                </>
            ) : section.body ? (
                <>
                    <p className="m-0 whitespace-pre-line text-[14px] leading-[1.6] text-[var(--text-reading)] [text-wrap:pretty]">
                        {section.body}
                    </p>
                    {section.kind === 'guide' ? (
                        <p className="m-0 mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                            {reportCopy.guideFixed}
                        </p>
                    ) : null}
                </>
            ) : empty ? (
                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {empty}
                </p>
            ) : null}

            {saveError ? (
                <p role="alert" className="m-0 mt-2 text-[13px] leading-[1.5] text-[var(--danger)]">
                    {saveError}
                </p>
            ) : null}
        </section>
    );
}
